import React, { useRef, useState, useEffect, useCallback } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Avatar,
  Badge,
  Fab,
  ImageList,
  ImageListItem,
  Switch,
  FormControlLabel,
  LinearProgress,
  CircularProgress,
} from "@mui/material";
import {
  PhotoCamera,
  CameraAlt,
  FlipCameraIos,
  LocationOn,
  LocationOff,
  GridOn,
  GridOff,
  Delete,
  Close,
  Check,
  Warning,
  Info,
  Schedule,
  Image,
  CloudUpload,
  ArrowBack,
  ArrowForward,
  Visibility,
} from "@mui/icons-material";

const PpMobilePhotoCaptureComponent = ({
  open,
  onClose,
  onPhotosUploaded,
  category = "inspection",
  inspectionId = null,
  propertyId = null,
  requiredPhotos = [],
}) => {
  // Camera state
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const initializedRef = useRef(false);
  const captureInFlightRef = useRef(false);
  const [cameraActive, setCameraActive] = useState(false);
  const [facingMode, setFacingMode] = useState("environment"); // "user" for front, "environment" for back
  const [gridLines, setGridLines] = useState(true);

  // Location state
  const [locationEnabled, setLocationEnabled] = useState(true);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [locationAccuracy, setLocationAccuracy] = useState(null);

  // Photo capture state
  const [capturedPhotos, setCapturedPhotos] = useState([]);
  const [isCapturing, setIsCapturing] = useState(false);
  const [timerMode, setTimerMode] = useState(0); // 0 = off, 3 = 3s, 5 = 5s, 10 = 10s
  const [timerCountdown, setTimerCountdown] = useState(0);
  const [autoCapture, setAutoCapture] = useState(false);
  const [captureInterval] = useState(5000); // 5 seconds
  const lastCaptureTimeRef = useRef(0);

  // Upload state
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [photoNotes, setPhotoNotes] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(category);

  // Quality settings
  const [photoQuality, setPhotoQuality] = useState(0.8);
  const [photoSize, setPhotoSize] = useState({ width: 1920, height: 1080 });
  const [compressionLevel, setCompressionLevel] = useState("medium");

  // Validation state
  const [validationResults, setValidationResults] = useState([]);
  const [validationMode, setValidationMode] = useState(true);
  const [photoPreviewOpen, setPhotoPreviewOpen] = useState(false);
  const [fullSizeViewerOpen, setFullSizeViewerOpen] = useState(false);
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(0);

  const photoCategories = [
    { value: "inspection", label: "Inspection Photos", icon: <CameraAlt /> },
    {
      value: "property",
      label: "Property Documentation",
      icon: <LocationOn />,
    },
    { value: "compliance", label: "Code Compliance", icon: <Check /> },
    { value: "issues", label: "Issues & Problems", icon: <Warning /> },
    { value: "progress", label: "Progress Documentation", icon: <Schedule /> },
    { value: "reference", label: "Reference Materials", icon: <Info /> },
  ];

  // Initialize camera
  const initializeCamera = useCallback(async () => {
    try {
      // Stop existing stream first
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }

      const constraints = {
        video: {
          facingMode,
          width: { ideal: photoSize.width },
          height: { ideal: photoSize.height },
        },
        audio: false,
      };

      const newStream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = newStream;

      if (videoRef.current) {
        videoRef.current.srcObject = newStream;

        // Wait for video to be ready before playing
        videoRef.current.onloadedmetadata = () => {
          if (videoRef.current) {
            videoRef.current.play().catch((playError) => {
              console.warn("Video play was interrupted:", playError);
              // This is expected behavior when video is being reloaded
            });
          }
        };

        setCameraActive(true);
      }
    } catch (error) {
      console.error("Error accessing camera:", error);
      alert(`Camera access failed: ${error.message}`);
    }
  }, [facingMode, photoSize]);

  // Get current location
  const getCurrentLocation = useCallback(() => {
    if (!locationEnabled) return;

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setCurrentLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
            timestamp: new Date().toISOString(),
          });
          setLocationAccuracy(position.coords.accuracy);
        },
        (error) => {
          console.error("Location error:", error);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 60000,
        }
      );
    }
  }, [locationEnabled]);

  const validatePhoto = useCallback(
    async (photoData) => {
      // Mock validation - in real app this would use AI or other validation methods
      const validations = [];

      // Validate photoData and blob exist
      if (!photoData || !photoData.blob) {
        validations.push({
          type: "error",
          message: "Photo data is invalid or corrupted.",
        });
        setValidationResults((prev) => [
          ...prev,
          {
            photoId: photoData?.id || "unknown",
            validations: validations,
          },
        ]);
        return;
      }

      // Check file size
      if (photoData.blob.size < 100000) {
        // Less than 100KB
        validations.push({
          type: "warning",
          message:
            "Photo file size is quite small. Consider higher quality setting.",
        });
      }

      // Check GPS accuracy
      if (locationEnabled && (!currentLocation || locationAccuracy > 100)) {
        validations.push({
          type: "warning",
          message:
            "GPS accuracy is poor. Consider taking photo in better location reception area.",
        });
      }

      // Check if it's a required photo type
      if (requiredPhotos.length > 0) {
        const isRequired = requiredPhotos.some(
          (req) => req.category === selectedCategory
        );
        if (isRequired) {
          validations.push({
            type: "success",
            message: "This photo type is required for the inspection.",
          });
        }
      }

      // Check timestamp (should be recent)
      const photoTime = new Date(photoData.timestamp);
      const now = new Date();
      const timeDiff = Math.abs(now - photoTime) / (1000 * 60); // minutes

      if (timeDiff > 5) {
        validations.push({
          type: "warning",
          message: "Photo timestamp suggests this was not taken recently.",
        });
      }

      setValidationResults((prev) => [
        ...prev,
        {
          photoId: photoData.id,
          validations: validations,
        },
      ]);
    },
    [
      locationEnabled,
      currentLocation,
      locationAccuracy,
      requiredPhotos,
      selectedCategory,
    ]
  );

  const capturePhotoActual = useCallback(async () => {
    if (!videoRef.current || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const video = videoRef.current;
    const context = canvas.getContext("2d");

    // Set canvas size to match video
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Draw video frame to canvas
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Convert to blob with quality setting
    canvas.toBlob(
      (blob) => {
        // Validate blob was created successfully
        if (!blob) {
          console.error("Failed to create photo blob");
          captureInFlightRef.current = false;
          return;
        }

        const photoData = {
          id: `PHOTO_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          blob,
          dataUrl: canvas.toDataURL("image/jpeg", photoQuality),
          timestamp: new Date().toISOString(),
          location: currentLocation,
          metadata: {
            camera: "Mobile Camera",
            facingMode,
            quality: photoQuality,
            size: `${canvas.width}x${canvas.height}`,
            fileSize: blob.size,
            gpsAccuracy: locationAccuracy,
          },
          category: selectedCategory,
          notes: "",
          validated: false,
        };

        setCapturedPhotos((prev) => [...prev, photoData]);

        // Validate photo if validation mode is enabled
        if (validationMode) {
          validatePhoto(photoData);
        }

        captureInFlightRef.current = false;
      },
      "image/jpeg",
      photoQuality
    );
  }, [
    currentLocation,
    facingMode,
    photoQuality,
    selectedCategory,
    validationMode,
    locationAccuracy,
    validatePhoto,
  ]);

  const capturePhoto = useCallback(() => {
    const now = Date.now();
    const timeSinceLastCapture = now - lastCaptureTimeRef.current;

    // Prevent multiple rapid captures (minimum 500ms between captures)
    if (
      isCapturing ||
      captureInFlightRef.current ||
      timeSinceLastCapture < 500
    ) {
      return;
    }

    lastCaptureTimeRef.current = now;

    if (timerMode > 0) {
      setIsCapturing(true);
      captureInFlightRef.current = true;
      setTimerCountdown(timerMode);
    } else {
      // Direct capture without toggling UI state to avoid flashing
      captureInFlightRef.current = true;
      capturePhotoActual();
    }
  }, [timerMode, capturePhotoActual, isCapturing]);

  // Initialize on mount
  useEffect(() => {
    if (!open) return;

    // Capture ref values at the beginning of the effect
    const videoElement = videoRef.current;
    const currentStream = streamRef.current;

    let cancelled = false;
    (async () => {
      if (cancelled) return;
      if (!initializedRef.current) {
        initializedRef.current = true;
        await initializeCamera();
        getCurrentLocation();
      }
    })();

    return () => {
      cancelled = true;
      // Cleanup function to prevent race conditions
      if (currentStream) {
        currentStream.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }

      // Clear video element
      if (videoElement) {
        videoElement.srcObject = null;
        videoElement.onloadedmetadata = null;
      }
      initializedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Cleanup camera when modal closes
  useEffect(() => {
    if (!open) {
      // Stop camera stream when modal is closed
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => {
          track.stop();
        });
        streamRef.current = null;
      }

      // Clear video element
      if (videoRef.current) {
        if (videoRef.current.srcObject) {
          const videoStream = videoRef.current.srcObject;
          if (videoStream && videoStream.getTracks) {
            videoStream.getTracks().forEach((track) => {
              track.stop();
            });
          }
        }
        videoRef.current.srcObject = null;
        videoRef.current.onloadedmetadata = null;
      }

      // Reset camera state
      setCameraActive(false);
      initializedRef.current = false;
    }
  }, [open]);

  // Cleanup on component unmount
  useEffect(() => {
    const cleanupCamera = () => {
      // Final cleanup when component unmounts
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => {
          track.stop();
        });
        streamRef.current = null;
      }

      if (videoRef.current && videoRef.current.srcObject) {
        const videoStream = videoRef.current.srcObject;
        if (videoStream && videoStream.getTracks) {
          videoStream.getTracks().forEach((track) => {
            track.stop();
          });
        }
        videoRef.current.srcObject = null;
      }
    };

    // Add beforeunload listener to cleanup camera when page is about to unload
    const handleBeforeUnload = () => {
      cleanupCamera();
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      cleanupCamera();
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  // Auto-capture interval
  useEffect(() => {
    let interval = null;
    if (autoCapture && cameraActive) {
      interval = setInterval(() => {
        capturePhoto();
      }, captureInterval);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoCapture, cameraActive, captureInterval, capturePhoto]);

  // Timer countdown
  useEffect(() => {
    let timeout = null;
    if (timerCountdown > 0) {
      timeout = setTimeout(() => {
        setTimerCountdown(timerCountdown - 1);
      }, 1000);
    } else if (timerCountdown === 0 && isCapturing) {
      capturePhotoActual();
      setIsCapturing(false);
      captureInFlightRef.current = false;
    }
    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [timerCountdown, isCapturing, capturePhotoActual]);

  const removePhoto = (photoId) => {
    setCapturedPhotos((prev) => prev.filter((p) => p.id !== photoId));
    setValidationResults((prev) => prev.filter((v) => v.photoId !== photoId));
  };

  const switchCamera = () => {
    // Prevent rapid camera switching
    if (cameraActive) {
      setCameraActive(false);
      setTimeout(() => {
        setFacingMode((prev) => (prev === "user" ? "environment" : "user"));
      }, 100);
    } else {
      setFacingMode((prev) => (prev === "user" ? "environment" : "user"));
    }
  };

  const adjustQualitySettings = (level) => {
    setCompressionLevel(level);
    switch (level) {
      case "low":
        setPhotoQuality(0.5);
        setPhotoSize({ width: 1280, height: 720 });
        break;
      case "medium":
        setPhotoQuality(0.8);
        setPhotoSize({ width: 1920, height: 1080 });
        break;
      case "high":
        setPhotoQuality(0.95);
        setPhotoSize({ width: 3840, height: 2160 });
        break;
      default:
        setPhotoQuality(0.8);
        setPhotoSize({ width: 1920, height: 1080 });
        break;
    }
  };

  const handleUpload = async () => {
    if (capturedPhotos.length === 0) return;

    try {
      setUploading(true);
      setUploadProgress(0);

      // Mock upload process with progress
      for (let i = 0; i < capturedPhotos.length; i++) {
        // Simulate upload delay
        await new Promise((resolve) => setTimeout(resolve, 1000));

        // Update progress
        setUploadProgress(((i + 1) / capturedPhotos.length) * 100);
      }

      // Notify parent component
      if (onPhotosUploaded) {
        onPhotosUploaded(
          capturedPhotos.map((photo) => ({
            ...photo,
            notes: photoNotes,
          }))
        );
      }

      // Reset state
      setCapturedPhotos([]);
      setPhotoNotes("");
      setValidationResults([]);
      handleClose();
    } catch (error) {
      console.error("Upload failed:", error);
      alert("Upload failed. Please try again.");
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const openFullSizeViewer = (photoIndex) => {
    setSelectedPhotoIndex(photoIndex);
    setFullSizeViewerOpen(true);
  };

  const navigateToPrevious = () => {
    setSelectedPhotoIndex((prev) =>
      prev === 0 ? capturedPhotos.length - 1 : prev - 1
    );
  };

  const navigateToNext = () => {
    setSelectedPhotoIndex((prev) =>
      prev === capturedPhotos.length - 1 ? 0 : prev + 1
    );
  };

  const getValidationIcon = (validations) => {
    if (validations.some((v) => v.type === "error"))
      return <Warning color="error" />;
    if (validations.some((v) => v.type === "warning"))
      return <Warning color="warning" />;
    return <Check color="success" />;
  };

  // Clean up camera stream and close modal
  const handleClose = useCallback(() => {
    // Stop camera stream
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => {
        track.stop();
      });
      streamRef.current = null;
    }

    // Also check video element's srcObject
    if (videoRef.current && videoRef.current.srcObject) {
      const videoStream = videoRef.current.srcObject;
      if (videoStream && videoStream.getTracks) {
        videoStream.getTracks().forEach((track) => {
          track.stop();
        });
      }
      videoRef.current.srcObject = null;
      videoRef.current.onloadedmetadata = null;
    }

    // Reset camera state
    setCameraActive(false);
    initializedRef.current = false;

    // Close modal
    onClose();
  }, [onClose]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      fullScreen // Better for mobile
      PaperProps={{
        sx: {
          height: "100vh",
          m: 0,
        },
      }}
    >
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <PhotoCamera color="primary" />
            <Typography variant="h6">Mobile Photo Capture</Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IconButton
              onClick={() => setPhotoPreviewOpen(true)}
              size="small"
              disabled={capturedPhotos.length === 0}
              sx={{
                opacity: capturedPhotos.length === 0 ? 0.5 : 1,
                cursor: capturedPhotos.length === 0 ? "not-allowed" : "pointer",
              }}
            >
              <Badge badgeContent={capturedPhotos.length} color="primary">
                <Image />
              </Badge>
            </IconButton>
            <IconButton onClick={handleClose} size="small">
              <Close />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 1, position: "relative" }}>
        {/* Camera View */}
        <Box
          sx={{
            position: "relative",
            mb: 2,
            height: "50vh",
            bgcolor: "black",
            borderRadius: 1,
          }}
        >
          <video
            ref={videoRef}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              borderRadius: 4,
            }}
            playsInline
            muted
          />

          <canvas ref={canvasRef} style={{ display: "none" }} />

          {/* Grid lines overlay */}
          {gridLines && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundImage: `
                  linear-gradient(rgba(255,255,255,0.3) 1px, transparent 1px),
                  linear-gradient(90deg, rgba(255,255,255,0.3) 1px, transparent 1px)
                `,
                backgroundSize: "33.333% 33.333%",
                pointerEvents: "none",
              }}
            />
          )}

          {/* Timer countdown overlay */}
          {timerCountdown > 0 && (
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                color: "white",
                fontSize: "4rem",
                fontWeight: "bold",
                textShadow: "0 0 10px rgba(0,0,0,0.8)",
              }}
            >
              {timerCountdown}
            </Box>
          )}

          {/* Location indicator */}
          {locationEnabled && (
            <Box
              sx={{
                position: "absolute",
                top: 8,
                left: 8,
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                bgcolor: "rgba(0,0,0,0.6)",
                color: "white",
                px: 1,
                py: 0.5,
                borderRadius: 1,
                fontSize: "0.75rem",
              }}
            >
              {currentLocation ? (
                <>
                  <LocationOn fontSize="small" />
                  <Typography variant="caption">
                    GPS:{" "}
                    {locationAccuracy
                      ? `±${Math.round(locationAccuracy)}m`
                      : "Active"}
                  </Typography>
                </>
              ) : (
                <>
                  <LocationOff fontSize="small" />
                  <Typography variant="caption">No GPS</Typography>
                </>
              )}
            </Box>
          )}

          {/* Quality indicator */}
          <Box
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              bgcolor: "rgba(0,0,0,0.6)",
              color: "white",
              px: 1,
              py: 0.5,
              borderRadius: 1,
              fontSize: "0.75rem",
            }}
          >
            <Typography variant="caption">
              {compressionLevel.toUpperCase()} • {photoSize.width}x
              {photoSize.height}
            </Typography>
          </Box>
        </Box>

        {/* Camera Controls */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
            mb: 2,
          }}
        >
          <IconButton onClick={() => setGridLines(!gridLines)}>
            {gridLines ? <GridOn /> : <GridOff />}
          </IconButton>

          <IconButton onClick={() => setLocationEnabled(!locationEnabled)}>
            {locationEnabled ? <LocationOn color="primary" /> : <LocationOff />}
          </IconButton>

          <IconButton onClick={switchCamera}>
            <FlipCameraIos />
          </IconButton>

          {/* Timer mode selector */}
          <FormControl size="small" sx={{ minWidth: 80 }}>
            <Select
              value={timerMode}
              onChange={(e) => setTimerMode(e.target.value)}
              displayEmpty
              variant="outlined"
            >
              <MenuItem value={0}>No Timer</MenuItem>
              <MenuItem value={3}>3s</MenuItem>
              <MenuItem value={5}>5s</MenuItem>
              <MenuItem value={10}>10s</MenuItem>
            </Select>
          </FormControl>

          {/* Capture button */}
          <Fab
            color="primary"
            onClick={capturePhoto}
            disabled={!cameraActive || isCapturing}
            disableRipple
            disableFocusRipple
            sx={{
              mx: 2,
              ...(!cameraActive && {
                opacity: 0.5,
              }),
            }}
          >
            {isCapturing ? (
              <CircularProgress size={24} sx={{ color: "white" }} />
            ) : (
              <PhotoCamera />
            )}
          </Fab>

          {/* Quality settings */}
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <Select
              value={compressionLevel}
              onChange={(e) => adjustQualitySettings(e.target.value)}
              variant="outlined"
            >
              <MenuItem value="low">Low</MenuItem>
              <MenuItem value="medium">Medium</MenuItem>
              <MenuItem value="high">High</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Settings Row */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
            px: 1,
          }}
        >
          <FormControlLabel
            control={
              <Switch
                checked={autoCapture}
                onChange={(e) => setAutoCapture(e.target.checked)}
              />
            }
            label={`Auto: ${captureInterval / 1000}s`}
            labelPlacement="end"
          />

          <FormControlLabel
            control={
              <Switch
                checked={validationMode}
                onChange={(e) => setValidationMode(e.target.checked)}
              />
            }
            label="Validation"
            labelPlacement="end"
          />

          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Category</InputLabel>
            <Select
              value={selectedCategory}
              label="Category"
              onChange={(e) => setSelectedCategory(e.target.value)}
            >
              {photoCategories.map((cat) => (
                <MenuItem key={cat.value} value={cat.value}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {cat.icon}
                    <Typography variant="body2">{cat.label}</Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Captured Photos List */}
        {capturedPhotos.length > 0 && (
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="subtitle2" gutterBottom>
                Captured Photos ({capturedPhotos.length})
              </Typography>

              <List dense sx={{ maxHeight: 200, overflow: "auto" }}>
                {capturedPhotos.map((photo, index) => {
                  const validation = validationResults.find(
                    (v) => v.photoId === photo.id
                  );
                  return (
                    <ListItem key={photo.id}>
                      <ListItemIcon>
                        <Avatar
                          src={photo.dataUrl}
                          variant="rounded"
                          sx={{ width: 40, height: 40 }}
                        >
                          <Image />
                        </Avatar>
                      </ListItemIcon>

                      <ListItemText
                        primary={`Photo ${index + 1}`}
                        secondary={
                          <Box component="span" sx={{ display: "block" }}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              component="span"
                            >
                              {formatFileSize(photo.metadata.fileSize)} •{" "}
                              {photo.metadata.size}
                            </Typography>
                            {photo.location && (
                              <Typography
                                variant="caption"
                                display="block"
                                color="text.secondary"
                                component="span"
                              >
                                GPS: {photo.location.latitude.toFixed(6)},{" "}
                                {photo.location.longitude.toFixed(6)}
                              </Typography>
                            )}
                            {validation &&
                              validation.validations.length > 0 && (
                                <Box
                                  component="span"
                                  sx={{ mt: 0.5, display: "block" }}
                                >
                                  {validation.validations.map((v, idx) => (
                                    <Chip
                                      key={idx}
                                      label={v.message}
                                      size="small"
                                      color={
                                        v.type === "error"
                                          ? "error"
                                          : v.type === "warning"
                                            ? "warning"
                                            : "success"
                                      }
                                      sx={{ mr: 0.5, mb: 0.5 }}
                                    />
                                  ))}
                                </Box>
                              )}
                          </Box>
                        }
                      />

                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          ml: "auto",
                        }}
                      >
                        {validation &&
                          getValidationIcon(validation.validations)}
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => openFullSizeViewer(index)}
                          title="View full size"
                        >
                          <Visibility />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => removePhoto(photo.id)}
                          title="Delete photo"
                        >
                          <Delete />
                        </IconButton>
                      </Box>
                    </ListItem>
                  );
                })}
              </List>
            </CardContent>
          </Card>
        )}

        {/* Notes */}
        {capturedPhotos.length > 0 && (
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Photo Notes"
            value={photoNotes}
            onChange={(e) => setPhotoNotes(e.target.value)}
            placeholder="Add notes about these photos..."
            sx={{ mb: 2 }}
          />
        )}

        {/* Upload Progress */}
        {uploading && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Uploading photos... {Math.round(uploadProgress)}%
            </Typography>
            <LinearProgress variant="determinate" value={uploadProgress} />
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={uploading}>
          Cancel
        </Button>

        {capturedPhotos.length > 0 && (
          <Button
            onClick={() => setCapturedPhotos([])}
            disabled={uploading}
            color="error"
          >
            Clear All
          </Button>
        )}

        <Button
          variant="contained"
          onClick={handleUpload}
          disabled={capturedPhotos.length === 0 || uploading}
          startIcon={
            uploading ? <CircularProgress size={16} /> : <CloudUpload />
          }
        >
          {uploading
            ? "Uploading..."
            : `Upload ${capturedPhotos.length} Photo${capturedPhotos.length !== 1 ? "s" : ""}`}
        </Button>
      </DialogActions>

      {/* Photo Preview Dialog */}
      <Dialog
        open={photoPreviewOpen}
        onClose={() => setPhotoPreviewOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Image color="primary" />
            <Typography variant="h6">
              Captured Photos ({capturedPhotos.length})
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {capturedPhotos.length > 0 ? (
            <ImageList cols={3} rowHeight={200} sx={{ width: "100%" }}>
              {capturedPhotos.map((photo, index) => (
                <ImageListItem key={photo.id}>
                  <Box
                    sx={{
                      position: "relative",
                      height: 200,
                      cursor: "pointer",
                      "&:hover": {
                        "& .overlay": {
                          opacity: 1,
                        },
                      },
                    }}
                    onClick={() => openFullSizeViewer(index)}
                  >
                    <img
                      src={photo.dataUrl}
                      alt={`Captured ${index + 1}`}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        borderRadius: 4,
                      }}
                    />
                    <Box
                      className="overlay"
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        bgcolor: "rgba(0,0,0,0.7)",
                        color: "white",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        opacity: 0,
                        transition: "opacity 0.2s",
                        borderRadius: 4,
                      }}
                    >
                      <Typography variant="body2" textAlign="center">
                        Click to view full size
                      </Typography>
                      <Typography variant="caption" textAlign="center">
                        {photo.metadata.size} •{" "}
                        {formatFileSize(photo.metadata.fileSize)}
                      </Typography>
                    </Box>
                  </Box>
                </ImageListItem>
              ))}
            </ImageList>
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                py: 4,
              }}
            >
              <Image sx={{ fontSize: 80, color: "grey.400", mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                No photos captured yet
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPhotoPreviewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Full-Size Image Viewer Dialog */}
      <Dialog
        open={fullSizeViewerOpen}
        onClose={() => setFullSizeViewerOpen(false)}
        maxWidth={false}
        fullWidth
        fullScreen
        PaperProps={{
          sx: {
            bgcolor: "black",
            m: 0,
            maxHeight: "100vh",
          },
        }}
      >
        <DialogTitle
          sx={{
            bgcolor: "rgba(0,0,0,0.8)",
            color: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="h6">
            Photo {selectedPhotoIndex + 1} of {capturedPhotos.length}
          </Typography>
          <IconButton
            onClick={() => setFullSizeViewerOpen(false)}
            sx={{ color: "white" }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            p: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "black",
            position: "relative",
            minHeight: "calc(100vh - 120px)",
          }}
        >
          {capturedPhotos[selectedPhotoIndex] && (
            <>
              {/* Navigation Arrows */}
              {capturedPhotos.length > 1 && (
                <>
                  <IconButton
                    onClick={navigateToPrevious}
                    sx={{
                      position: "absolute",
                      left: 16,
                      top: "50%",
                      transform: "translateY(-50%)",
                      bgcolor: "rgba(0,0,0,0.5)",
                      color: "white",
                      "&:hover": {
                        bgcolor: "rgba(0,0,0,0.7)",
                      },
                    }}
                  >
                    <ArrowBack />
                  </IconButton>
                  <IconButton
                    onClick={navigateToNext}
                    sx={{
                      position: "absolute",
                      right: 16,
                      top: "50%",
                      transform: "translateY(-50%)",
                      bgcolor: "rgba(0,0,0,0.5)",
                      color: "white",
                      "&:hover": {
                        bgcolor: "rgba(0,0,0,0.7)",
                      },
                    }}
                  >
                    <ArrowForward />
                  </IconButton>
                </>
              )}

              {/* Main Image */}
              <img
                src={capturedPhotos[selectedPhotoIndex].dataUrl}
                alt={`Full size ${selectedPhotoIndex + 1}`}
                style={{
                  maxWidth: "100%",
                  maxHeight: "calc(100vh - 120px)",
                  objectFit: "contain",
                }}
              />

              {/* Photo Info Overlay */}
              <Box
                sx={{
                  position: "absolute",
                  bottom: 16,
                  left: 16,
                  right: 16,
                  bgcolor: "rgba(0,0,0,0.7)",
                  color: "white",
                  p: 2,
                  borderRadius: 1,
                }}
              >
                <Typography variant="body2">
                  <strong>Size:</strong>{" "}
                  {capturedPhotos[selectedPhotoIndex].metadata.size}
                </Typography>
                <Typography variant="body2">
                  <strong>File Size:</strong>{" "}
                  {formatFileSize(
                    capturedPhotos[selectedPhotoIndex].metadata.fileSize
                  )}
                </Typography>
                <Typography variant="body2">
                  <strong>Quality:</strong>{" "}
                  {capturedPhotos[selectedPhotoIndex].metadata.quality}
                </Typography>
                <Typography variant="body2">
                  <strong>Camera:</strong>{" "}
                  {capturedPhotos[selectedPhotoIndex].metadata.facingMode ===
                  "user"
                    ? "Front"
                    : "Back"}
                </Typography>
                {capturedPhotos[selectedPhotoIndex].location && (
                  <Typography variant="body2">
                    <strong>Location:</strong>{" "}
                    {capturedPhotos[
                      selectedPhotoIndex
                    ].location.latitude.toFixed(6)}
                    ,{" "}
                    {capturedPhotos[
                      selectedPhotoIndex
                    ].location.longitude.toFixed(6)}
                  </Typography>
                )}
                <Typography variant="body2">
                  <strong>Captured:</strong>{" "}
                  {new Date(
                    capturedPhotos[selectedPhotoIndex].timestamp
                  ).toLocaleString()}
                </Typography>
              </Box>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Global styles for animations */}
      <style>{`
        @keyframes pulse {
          0% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.05);
          }
          100% {
            transform: scale(1);
          }
        }
      `}</style>
    </Dialog>
  );
};

export default PpMobilePhotoCaptureComponent;
